<script setup lang="ts">
/**
 * UiSelect — нативный <select> с кастомным chevron (inbox-116).
 *
 * Options передаются:
 * — через prop `options: SelectOption[]` (typed)
 * — ИЛИ через default slot (свои <option>'ы)
 *
 * Error state — красная рамка + текст под полем.
 *
 * CSS портом из docs/mockups/settings-profile.html + lib/components.css
 * `.select` (общая стилизация с .input).
 *
 * Usage:
 *   <UiSelect v-model="role" :options="[{value:'admin', label:'Админ'}]" />
 *
 *   <UiSelect v-model="status">
 *     <option value="active">Активна</option>
 *     <option value="paused">На паузе</option>
 *   </UiSelect>
 */

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface Props {
  modelValue?: string | number;
  options?: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  name?: string;
}

withDefaults(defineProps<Props>(), {
  modelValue: '',
  options: () => [],
  placeholder: '',
  disabled: false,
  required: false,
  error: '',
  hint: '',
  name: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  change: [event: Event];
}>();

function onChange(e: Event): void {
  emit('update:modelValue', (e.target as HTMLSelectElement).value);
  emit('change', e);
}
</script>

<template>
  <div
    class="ui-select"
    :class="{ 'has-error': !!error, disabled }"
  >
    <div class="wrap">
      <select
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        :name="name"
        class="select"
        @change="onChange"
      >
        <option
          v-if="placeholder"
          value=""
          disabled
        >
          {{ placeholder }}
        </option>
        <template v-if="options.length > 0">
          <option
            v-for="opt in options"
            :key="opt.value"
            :value="opt.value"
            :disabled="opt.disabled"
          >
            {{ opt.label }}
          </option>
        </template>
        <slot v-else />
      </select>
      <svg
        class="chevron"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>

    <p
      v-if="error"
      class="error-msg"
    >
      {{ error }}
    </p>
    <p
      v-else-if="hint"
      class="hint-msg"
    >
      {{ hint }}
    </p>
  </div>
</template>

<style scoped>
.ui-select {
  display: block;
  width: 100%;
}

.wrap {
  position: relative;
}

/* inbox-330 — единый h=40 / r=8 / fz=14 с UiInput. */
.select {
  width: 100%;
  min-height: 40px;
  background: var(--color-bg-elev);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-input);
  padding: 10px 36px 10px 14px;
  color: var(--color-fg);
  font: inherit;
  font-size: var(--fs-body);
  line-height: 1.4;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, box-shadow 0.12s;
}
.select:focus {
  box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.15);
}
.select:focus {
  outline: 0;
  border-color: var(--color-accent);
}
.select:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.select option {
  background: var(--color-bg-card);
  color: var(--color-fg);
}

.has-error .select {
  border-color: var(--color-danger);
}
.has-error .select:focus {
  border-color: var(--color-danger);
}

.chevron {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  color: var(--color-fg-dim);
  pointer-events: none;
}

.error-msg {
  margin-top: 4px;
  font-size: var(--fs-small);
  color: var(--color-danger);
}
.hint-msg {
  margin-top: 4px;
  font-size: var(--fs-small);
  color: var(--color-fg-dim);
}
</style>
